import dayjs from 'dayjs';

const NBSP = ' ';

/** Formats an ISO date(-time) string as `DD.MM.YYYY`. */
export const formatDate = (value: string | null | undefined): string | null =>
  value ? dayjs(value).format('DD.MM.YYYY') : null;

/** Formats an ISO date-time string as `DD.MM.YYYY, HH:mm:ss`. */
export const formatDateTime = (
  value: string | null | undefined
): string | null =>
  value ? dayjs(value).format('DD.MM.YYYY, HH:mm:ss') : null;

/** Groups integer digits with non-breaking spaces: 8432 → "8 432". */
export const formatThousands = (
  value: number | null | undefined
): string | null => {
  if (value == null || !Number.isFinite(value)) return null;
  const [int = '', frac] = Math.abs(value).toString().split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const sign = value < 0 ? '-' : '';
  return `${sign}${grouped}${frac ? `,${frac}` : ''}`;
};

/**
 * Formats a second offset as a clock: 148 → "2:28", 3750 → "1:02:30".
 *
 * `alwaysHours` keeps the hour part on short values (148 → "0:02:28"), for
 * durations that read better in a fixed shape.
 */
export const formatSecondsClock = (
  value: number | null | undefined,
  { alwaysHours = false }: { alwaysHours?: boolean } = {}
): string | null => {
  if (value == null || !Number.isFinite(value)) return null;
  const total = Math.max(0, Math.trunc(value));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (part: number) => String(part).padStart(2, '0');
  return hours > 0 || alwaysHours
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

/** Formats a second count verbosely: 148 → "2 min 28 s", 3750 → "1 h 2 min". */
export const formatSecondsVerbose = (
  value: number | null | undefined
): string | null => {
  if (value == null || !Number.isFinite(value)) return null;
  const total = Math.max(0, Math.trunc(value));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}${NBSP}h`);
  if (minutes > 0) parts.push(`${minutes}${NBSP}min`);
  // Seconds only matter below the hour mark — and carry the zero case.
  if (hours === 0 && (seconds > 0 || minutes === 0))
    parts.push(`${seconds}${NBSP}s`);
  return parts.join(' ');
};

/**
 * Formats a half-open second range as `2:28 → 4:31 (2 min 3 s)`; the length is
 * dropped for empty ranges or when `withoutLength` is set.
 */
export const formatSecondsRange = (
  value: { lower: number; upper: number },
  { withoutLength = false }: { withoutLength?: boolean } = {}
): string => {
  const length = value.upper - value.lower;
  const range = `${formatSecondsClock(value.lower)} → ${formatSecondsClock(value.upper)}`;
  return !withoutLength && length > 0
    ? `${range} (${formatSecondsVerbose(length)})`
    : range;
};

/** Formats a fraction rounded to 4 decimal places: "0.86663" → "0.8666". */
export const formatFraction = (value: string | number): string =>
  Number(value).toFixed(4);

/**
 * Formats a unit count (full copies, up to 4 places) with grouped digits and
 * no trailing zeros: "1234.5000" → "1 234,5".
 */
export const formatUnits = (
  value: string | number | null | undefined
): string | null =>
  value == null || value === ''
    ? null
    : formatThousands(Number(Number(value).toFixed(4)));

/** Formats a rating with a comma decimal separator: 4.6 → "4,6". */
export const formatRating = (
  value: number | null | undefined
): string | null => (value == null ? null : value.toFixed(1).replace('.', ','));

/**
 * Formats a byte count with base-1024 units and a comma decimal
 * separator: 4404019 → "4,2 MB".
 */
export const formatFileSizeComma = (
  bytes: number | null | undefined
): string | null => {
  if (bytes == null || !Number.isFinite(bytes)) return null;
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const text = unit === 0 ? String(value) : value.toFixed(1).replace('.', ',');
  return `${text}${NBSP}${units[unit]}`;
};

/** Formats minutes verbosely: 748 → "12 h 28 min (748 min)". */
export const formatMinutesVerbose = (
  minutes: number | null | undefined
): string | null => {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} h`);
  if (rest > 0 || hours === 0) parts.push(`${rest} min`);
  return `${parts.join(' ')} (${formatThousands(minutes)} min)`;
};

/**
 * Abbreviates given names to initials, keeping the surname:
 * "Jan Kowalski" → "J. Kowalski", "Anna Maria Nowak" → "A. M. Nowak".
 */
export const abbreviatePersonName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name.trim();
  const surname = parts[parts.length - 1];
  const initials = parts
    .slice(0, -1)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join(' ');
  return `${initials} ${surname}`;
};
